/**
 * <request-size> — lets a customer request an unlisted size as a backorder.
 *
 * Adds the product's first available variant to the cart with two
 * line-item properties:
 *   - "Size requested": <user input>
 *   - "Backorder": "Yes"
 * and appends "[Backorder request: <size>]" to the cart note.
 */
class RequestSize extends HTMLElement {
  constructor() {
    super();

    this.toggle = this.querySelector('[data-request-toggle]');
    this.panel = this.querySelector('[data-request-panel]');
    this.input = this.querySelector('[data-request-input]');
    this.ack = this.querySelector('[data-request-ack]');
    this.submit = this.querySelector('[data-request-submit]');
    this.errorEl = this.querySelector('[data-request-error]');
    this.fallbackVariantId = this.dataset.fallbackVariantId;

    if (!this.toggle || !this.panel || !this.input || !this.ack || !this.submit) return;

    this.onToggle = this.onToggle.bind(this);
    this.onChange = this.onChange.bind(this);
    this.onSubmit = this.onSubmit.bind(this);

    this.toggle.addEventListener('click', this.onToggle);
    this.input.addEventListener('input', this.onChange);
    this.ack.addEventListener('change', this.onChange);
    this.submit.addEventListener('click', this.onSubmit);
  }

  onToggle() {
    const open = !this.panel.hidden;
    this.panel.hidden = open;
    this.toggle.setAttribute('aria-expanded', String(!open));
  }

  onChange() {
    const valid = this.input.value.trim().length > 0 && this.ack.checked;
    this.submit.disabled = !valid;
    this.hideError();
  }

  showError(msg) {
    if (!this.errorEl) return;
    this.errorEl.textContent = msg;
    this.errorEl.hidden = false;
  }

  hideError() {
    if (!this.errorEl) return;
    this.errorEl.hidden = true;
    this.errorEl.textContent = '';
  }

  async onSubmit() {
    const size = this.input.value.trim();
    if (!size || !this.ack.checked || !this.fallbackVariantId) return;

    // Read quantity from the section's quantity selector if present.
    const sectionId = this.dataset.sectionId;
    let quantity = 1;
    const qtyInput = document.getElementById(`Quantity-${sectionId}`);
    if (qtyInput && qtyInput.value) {
      const parsed = parseInt(qtyInput.value, 10);
      if (parsed > 0) quantity = parsed;
    }

    this.submit.disabled = true;
    this.submit.setAttribute('aria-disabled', 'true');
    this.submit.classList.add('loading');
    const spinner = this.submit.querySelector('.loading__spinner');
    if (spinner) spinner.classList.remove('hidden');
    const originalLabel = this.submit.querySelector('span')?.textContent;

    try {
      const root = window.Shopify?.routes?.root || '/';

      // Use whichever cart presenter the theme is configured for —
      // <cart-notification> (popup) or <cart-drawer> (slide-out).
      const cartPresenter =
        document.querySelector('cart-notification') || document.querySelector('cart-drawer');
      const sectionsToFetch = cartPresenter
        ? cartPresenter.getSectionsToRender().map((s) => s.id)
        : ['cart-icon-bubble'];

      // 1) Add the line item with properties + ask Shopify to re-render
      //    the cart sections in the same response.
      const addRes = await fetch(`${root}cart/add.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          items: [
            {
              id: parseInt(this.fallbackVariantId, 10),
              quantity: quantity,
              properties: {
                'Size requested': size,
                Backorder: 'Yes',
              },
            },
          ],
          sections: sectionsToFetch.join(','),
          sections_url: window.location.pathname,
        }),
      });

      if (!addRes.ok) {
        const err = await addRes.json().catch(() => ({}));
        throw new Error(err.description || err.message || 'Could not add backorder request to cart.');
      }

      const addPayload = await addRes.json();
      // /cart/add.js with multiple items returns { items: [...] };
      // single-item adds return the line item itself. Normalize.
      const addedItem =
        addPayload.items && addPayload.items.length ? addPayload.items[0] : addPayload;

      // 2) Note is now auto-managed by cart-backorder-note.js, which
      //    rebuilds a delimited "--- Backorder requests ---" block on
      //    every cart update.

      // 3) Hand off to the cart presenter so it shows its standard
      //    notification/drawer animation.
      if (cartPresenter && addPayload.sections) {
        cartPresenter.renderContents({
          id: parseInt(this.fallbackVariantId, 10),
          key: addedItem && addedItem.key,
          sections: addPayload.sections,
        });
      } else if (addPayload.sections) {
        // No presenter at all: just refresh the cart count bubble.
        const bubbleHtml = addPayload.sections['cart-icon-bubble'];
        if (bubbleHtml) {
          const bubble = document.getElementById('cart-icon-bubble');
          const fresh = new DOMParser().parseFromString(bubbleHtml, 'text/html').querySelector('#cart-icon-bubble');
          if (bubble && fresh) bubble.innerHTML = fresh.innerHTML;
        }
      }

      // Notify the cart hover preview to refresh too.
      document.dispatchEvent(new CustomEvent('cart:refresh'));
      document.dispatchEvent(new CustomEvent('cart-update'));

      // Reset form and close.
      this.input.value = '';
      this.ack.checked = false;
      this.submit.disabled = true;
      this.panel.hidden = true;
      this.toggle.setAttribute('aria-expanded', 'false');
    } catch (e) {
      this.showError(e.message || 'Could not add backorder request to cart.');
      this.submit.disabled = false;
      if (originalLabel) {
        const span = this.submit.querySelector('span');
        if (span) span.textContent = originalLabel;
      }
    } finally {
      this.submit.classList.remove('loading');
      this.submit.removeAttribute('aria-disabled');
      const spinnerEl = this.submit.querySelector('.loading__spinner');
      if (spinnerEl) spinnerEl.classList.add('hidden');
    }
  }
}

customElements.define('request-size', RequestSize);
